import { useState } from 'react'
import type { NtCourse, NtTerm, NtBlock } from '../../core/types'
import { assignBlock } from './model'
import { Empty, ErrorState } from '../../ui/States'

/**
 * Blocks — the coordinator decides which courses run in the morning and which
 * in the afternoon, before placement can run at all.
 *
 * The number that matters is at the top: total seats per block against the
 * number of students. Split the courses wrong and placement "works" while
 * forty kids get filled into whatever had room, which looks like a software
 * problem and is not one.
 *
 * Placement itself lands next — the engine and its tests are already in
 * src/modules/norseterm/placement.ts.
 */
export function Blocks({ term, courses }: { term: NtTerm; courses: NtCourse[] }) {
  const [err, setErr] = useState<string | null>(null)
  const approved = courses.filter((c) => c.status === 'approved')

  const seatsIn = (b: NtBlock) =>
    approved.filter((c) => c.block === b).reduce((n, c) => n + c.cap, 0)
  const unassigned = approved.filter((c) => !c.block)

  const set = (id: string, block: NtBlock | null) =>
    assignBlock(id, block).catch((e) => setErr(String(e)))

  if (approved.length === 0) {
    return <Empty title="Nothing approved yet" body="Courses have to be approved before they can be scheduled into a block." />
  }

  return (
    <>
      {err && <ErrorState body={`Could not save. ${err}`} />}

      <div className="card" style={{ marginTop: 'var(--s-2)' }}>
        <div className="dl">
          <div><div className="dl__k">Morning seats</div><div className="dl__v">{seatsIn('AM')}</div></div>
          <div><div className="dl__k">Afternoon seats</div><div className="dl__v">{seatsIn('PM')}</div></div>
          <div><div className="dl__k">Unassigned</div><div className="dl__v">{unassigned.length}</div></div>
        </div>
        <p className="opt__hint">
          Every student needs one morning seat and one afternoon seat. If either number is
          below your enrolment, placement will fill people into whatever has room.
        </p>
      </div>

      {unassigned.length > 0 && (
        <div className="notice notice--warning" style={{ marginTop: 'var(--s-3)' }}>
          <span aria-hidden="true">⚠</span>
          <div>
            {unassigned.length} approved course{unassigned.length === 1 ? '' : 's'} still
            {unassigned.length === 1 ? ' has' : ' have'} no block. Placement skips them entirely.
          </div>
        </div>
      )}

      <ul className="work" style={{ marginTop: 'var(--s-3)' }}>
        {approved.map((c) => (
          <li key={c.id} className={`work__row${c.block ? '' : ' is-live'}`}>
            <div className="work__main">
              <div style={{ fontWeight: 610 }}>{c.title}</div>
              <div className="work__sub">
                {c.teacherName} · caps at {c.cap}
                {c.constraint !== 'none' && ` · asked for ${c.constraint === 'AM' ? 'morning' : 'afternoon'}`}
              </div>
            </div>
            <div className="chips" style={{ flex: '0 0 auto' }}>
              {(['AM', 'PM'] as NtBlock[]).map((b) => (
                <button key={b} className={`chip${c.block === b ? ' is-on' : ''}`}
                  onClick={() => set(c.id, c.block === b ? null : b)}>
                  {b}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>

      <p className="muted-note">
        Term: {term.label}. Running placement comes next. The engine is written and tested,
        it needs the student import screen in front of it.
      </p>
    </>
  )
}
